import type { MaybeRefOrGetter, Ref } from 'vue';
import { ref, computed, toValue } from 'vue';
import type { Organization } from '~/types/api/master/getOrganization';
import { MasterRepository } from '~/repository/MasterRepository';
import type { Station } from '~/types/api/master/getStation';
import { ClientError } from '~/error/ClientError';
import type { StationWithOrganization } from '~/types/common/stationWithOrganization';
import type { FrequentStation } from '~/types/common/frequentStation';
import { UserRepository } from '~/repository/UserRepository';
import { MessageCodeEnum } from '~/constants/MessageCode';
import { useFrequentStationLogic } from '~/composables/station/useFrequentStationLogic';
import { useSystemConfig } from '~/composables/common/useSystemConfig';

/**
 * StationSelectModalのロジックをまとめたcomposable
 *
 * @param displayId 画面ID
 * @param enableFrequentStations よく使う駅の記録・表示を有効にするかどうか
 *                               （代理ログインの開始・終了で切り替わるため、getterやrefで渡すこと）
 */
export const useStationSelectModalLogic = (
  displayId: string,
  enableFrequentStations: MaybeRefOrGetter<boolean> = false,
) => {
  const loginUserStore = useLoginUserStore();
  const { getPosition } = useGeoLocations();
  const { startLoad, endLoad } = useLoadingStore();
  const { getOrganizations, getStations } = useMasterStore();
  const { getStationAndOrganizationByStationCd } = useStation(displayId);
  const { recordSelection, getTopStations } = useFrequentStationLogic();
  const { getFrequentStationSettings } = useSystemConfig(displayId);
  const masterRepository = new MasterRepository(displayId);
  const userRepository = new UserRepository(displayId);

  // モーダルの開閉状態
  const isOpen = ref(false);

  // 選択した駅情報を親コンポーネントに通知するためのイベントを管理
  // emit関数を外部から注入する形で対応
  let stationSelectedCallback: ((_station: StationWithOrganization) => void) | null = null;
  let canceledCallback: (() => void) | null = null;

  /** 画面上で選択された支社 */
  const selectedOrganization: Ref<Organization | undefined> = ref();
  /** 画面上で選択された駅 */
  const selectedStation: Ref<Station | undefined> = ref();

  /** 組織一覧 */
  const organizations: Ref<Organization[]> = ref([]);
  /** 駅一覧 */
  const stations: Ref<Station[]> = ref([]);

  /** よく使う駅として表示するリスト（マスタに現存する駅のみ） */
  const frequentStations: Ref<FrequentStation[]> = ref([]);

  /** プルダウンに表示する支社一覧 */
  const filteredOrganizations = computed(() =>
    organizations.value.filter((org) => org.organizationKbn === '1'), // 支社のみ表示
  );

  /** プルダウンに表示する駅一覧（選択された支社コードで絞り込む） */
  const filteredStations = computed(() =>
    stations.value.filter((station) =>
      station.organizationCd === selectedOrganization.value?.organizationCd,
    ),
  );

  /**
   * APIを呼び出し、支社マスタと駅マスタを取得する
   */
  const _fetchMasterData = async (): Promise<void> => {
    organizations.value = await getOrganizations(displayId);
    stations.value = await getStations(displayId);
  };

  /**
   * フォームにデフォルト値を設定する
   *
   * @param defaultSelectStation 初期選択させたい駅情報
   */
  const _setDefaultSelect = async (defaultSelectStation?: StationWithOrganization): Promise<void> => {
    startLoad();
    try {
      let stationAndOrganization;
      // 駅情報が指定されてた場合は初期選択として設定
      if (defaultSelectStation) {
        stationAndOrganization = await getStationAndOrganizationByStationCd(
          defaultSelectStation.stationCd,
          defaultSelectStation.stationSubCd,
        );
      }
      // 初期選択駅情報未指定かつ、既にログインユーザーストアに作業駅が紐づいていたらデフォルトで選択状態にする
      else if (loginUserStore.loginUser?.workingStation) {
        stationAndOrganization = await getStationAndOrganizationByStationCd(
          loginUserStore.loginUser.workingStation.stationCd,
          loginUserStore.loginUser.workingStation.stationSubCd,
        );
      }
      // ストアに作業駅が設定されていない場合、PLATユーザ情報に登録されている作業駅をデフォルト選択とする
      else {
        const response = await userRepository.getUserInfo();
        stationAndOrganization = await getStationAndOrganizationByStationCd(
          response.data?.userInfo?.stationCd ?? '',
          response.data?.userInfo?.stationSubCd ?? '',
        );
      }

      selectedOrganization.value = stationAndOrganization?.organization;
      selectedStation.value = stationAndOrganization?.station;
    }
    finally {
      endLoad();
    }
  };

  /**
   * 現在位置から最寄り駅を検索して、作業駅として設定する
   */
  const selectNearestStation = async (): Promise<void> => {
    startLoad();
    try {
      // GeolocationAPIを使用して現在位置を取得
      // タイムアウト等の設定はuseGeolocations側でシステム設定から解決される
      const position = await getPosition(displayId);

      // 最寄り駅取得APIを呼び出す
      const nearestStation = (await masterRepository.getNearestStation({
        lat: position.coords.latitude,
        lon: position.coords.longitude,
      })).data.station;

      // 取得した最寄り駅情報に紐づくマスタ情報を取得
      const station = stations.value.find((st) =>
        st.stationCd === nearestStation.stationCd && st.stationSubCd === nearestStation.stationSubCd,
      );
      const organization = filteredOrganizations.value.find((org) =>
        org.organizationCd === nearestStation.organizationCd,
      );

      // 紐づくマスタ情報が無ければエラー
      if (!station || !organization) {
        throw new ClientError(MessageCodeEnum.NEAREST_STATION_NOT_FOUND, displayId);
      }

      // 最寄り駅情報を選択状態にする
      selectedOrganization.value = organization;
      selectedStation.value = station;
    }
    finally {
      endLoad();
    }
  };

  /**
   * 駅名プルダウンの選択状態を解除する。
   */
  const clearStationSelection = (): void => {
    selectedStation.value = undefined;
  };

  /**
   * よく使う駅の表示リストを読み込む。
   *
   * 履歴の上位（システム設定で指定した最大表示件数）のうち、取得済みマスタに現存する駅のみを表示対象とする。
   */
  const _loadFrequentStations = async (): Promise<void> => {
    if (!toValue(enableFrequentStations)) {
      frequentStations.value = [];
      return;
    }
    const { displayCount } = await getFrequentStationSettings();
    frequentStations.value = getTopStations(displayCount).filter((record) =>
      stations.value.some((station) =>
        station.stationCd === record.stationCd && station.stationSubCd === record.stationSubCd,
      ),
    );
  };

  /**
   * よく使う駅ボタンから駅を選択して即確定する。
   *
   * レコードが支社情報も保持しているため、マスタ検索なしでそのまま確定できる。
   *
   * @param record 選択されたよく使う駅レコード
   */
  const selectFrequentStation = (record: FrequentStation): void => {
    _selectStation({
      organizationCd: record.organizationCd,
      organizationName: record.organizationName,
      stationCd: record.stationCd,
      stationSubCd: record.stationSubCd,
      stationName: record.stationName,
      stationNameRyaku: record.stationNameRyaku,
    });
  };

  /**
   * モーダルを開く
   *
   * @param defaultSelectStation 初期選択させたい駅情報
   */
  const openModal = async (defaultSelectStation?: StationWithOrganization): Promise<void> => {
    await _fetchMasterData();
    await _setDefaultSelect(defaultSelectStation);
    await _loadFrequentStations();
    isOpen.value = true;
  };

  /**
   * モーダルを閉じる
   */
  const closeModal = (): void => {
    isOpen.value = false;
  };

  /**
   * 駅を確定する共通処理。よく使う駅が有効な場合は選択履歴を記録し、親コンポーネントへ通知する。
   *
   * @param stationData 確定する駅情報
   */
  const _selectStation = (stationData: StationWithOrganization): void => {
    // よく使う駅が有効な場合のみ選択履歴を記録する
    if (toValue(enableFrequentStations)) {
      recordSelection(stationData);
    }

    if (stationSelectedCallback) {
      stationSelectedCallback(stationData);
    }
  };

  /**
   * 駅選択処理のハンドラー
   */
  const handleStationSelection = async (): Promise<void> => {
    // 支店と駅のいずれかが未選択の場合は何もしない
    if (!selectedOrganization.value || !selectedStation.value) {
      return;
    }

    _selectStation({
      organizationCd: selectedOrganization.value.organizationCd,
      organizationName: selectedOrganization.value.organizationName,
      stationCd: selectedStation.value.stationCd,
      stationSubCd: selectedStation.value.stationSubCd,
      stationName: selectedStation.value.stationName,
      stationNameRyaku: selectedStation.value.stationNameRyaku,
    });
  };

  /**
   * stationSelectedイベントのコールバック関数を設定
   */
  const setStationSelectedCallback = (callback: (_station: StationWithOrganization) => void): void => {
    stationSelectedCallback = callback;
  };

  /**
   * canceledイベントのコールバック関数を設定
   */
  const setCanceledCallback = (callback: () => void): void => {
    canceledCallback = callback;
  };

  /**
   * キャンセルボタンのハンドラー
   */
  const handleCancel = (): void => {
    if (canceledCallback) {
      canceledCallback();
    }
  };

  return {
    // 状態
    isOpen,
    selectedOrganization,
    selectedStation,
    filteredOrganizations,
    filteredStations,
    frequentStations,

    // メソッド
    selectNearestStation,
    selectFrequentStation,
    clearStationSelection,
    openModal,
    closeModal,
    handleStationSelection,
    handleCancel,
    setStationSelectedCallback,
    setCanceledCallback,
  };
};
